// ─────────────────────────────────────────────────────────────────────────────
// LocationShippingExclusionsEffects
// ─────────────────────────────────────────────────────────────────────────────
// NgRx effects for the locationShippingExclusions slice (WB-7570).
// Load / Create / Delete against /v1/locations/shipping-exclusions, with
// typed 409-duplicate handling on create. Bulk create/delete hit the
// dedicated /bulk endpoint (one request for the whole batch, idempotent per
// id) -- an earlier version fanned out one request per id via forkJoin,
// which opened as many simultaneous connections as the batch had ids; with
// the "Select all N matching" picker action able to hand this hundreds or
// thousands of ids, that was enough to hang the tab and/or overwhelm the
// backend. A single bulk request sidesteps the problem entirely rather than
// just capping the fan-out's concurrency.
// ─────────────────────────────────────────────────────────────────────────────

import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, mergeMap, of, switchMap, tap } from 'rxjs';
import { APIResponse } from 'src/app/core/models/api-response';
import { CommonService } from 'src/app/shared/common.service';
import { LocationShippingExclusionService } from '../services/location-shipping-exclusion.service';
import { ILocationShippingExclusion } from '../models/location-shipping-exclusion.models';
import * as locationShippingExclusionsActions from './location-shipping-exclusions.actions';

@Injectable()
export class LocationShippingExclusionsEffects {
  constructor(
    private actions$: Actions,
    private locationShippingExclusionService: LocationShippingExclusionService,
    private commonService: CommonService,
    private translateService: TranslateService,
  ) {}

  // ── Load ───────────────────────────────────────────────────────────────────

  LoadLocationShippingExclusions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(locationShippingExclusionsActions.LoadLocationShippingExclusions),
      switchMap(() =>
        this.locationShippingExclusionService.list().pipe(
          map((response: APIResponse<ILocationShippingExclusion>) => locationShippingExclusionsActions.LoadLocationShippingExclusionsSuccess({ response })),
          catchError((error: HttpErrorResponse) =>
            of(locationShippingExclusionsActions.LoadLocationShippingExclusionsError({ error: this.resolveErrorMessage(error) })),
          ),
        ),
      ),
    ),
  );

  // ── Create ─────────────────────────────────────────────────────────────────

  CreateLocationShippingExclusion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(locationShippingExclusionsActions.CreateLocationShippingExclusion),
      mergeMap((action) =>
        this.locationShippingExclusionService.create(action.payload).pipe(
          map((response: APIResponse<ILocationShippingExclusion>) =>
            locationShippingExclusionsActions.CreateLocationShippingExclusionSuccess({ exclusion: response.object as ILocationShippingExclusion }),
          ),
          tap(() => {
            this.commonService.showToast(this.translateService.instant('knowledge_base.shipping_exclusions.toast.create_success'), 'SUCCESS');
          }),
          catchError((error: HttpErrorResponse) => {
            const message = this.resolveErrorMessage(error);
            this.commonService.showToast(message, 'ERROR');
            return of(locationShippingExclusionsActions.CreateLocationShippingExclusionError({ error: message }));
          }),
        ),
      ),
    ),
  );

  // ── Bulk create (one request for the whole batch, idempotent per id) ────────
  // A duplicate/already-excluded/invalid id is counted by the backend, never
  // raised as an error, so the whole batch always resolves to one Success
  // action -- there's no per-id partial-failure path to handle here anymore.

  BulkCreateLocationShippingExclusions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(locationShippingExclusionsActions.BulkCreateLocationShippingExclusions),
      mergeMap((action) =>
        this.locationShippingExclusionService.bulkCreate(action.locationIds).pipe(
          map(({ data, metadata }) =>
            locationShippingExclusionsActions.BulkCreateLocationShippingExclusionsSuccess({
              created: data,
              skipped: metadata.duplicates + metadata.invalid,
            }),
          ),
          tap(({ created, skipped }) => {
            if (created.length > 0) {
              this.commonService.showToast(
                this.translateService.instant('knowledge_base.shipping_exclusions.toast.bulk_create_success', { count: created.length }),
                'SUCCESS',
              );
            }
            if (skipped > 0) {
              this.commonService.showToast(
                this.translateService.instant('knowledge_base.shipping_exclusions.toast.bulk_create_skipped', { count: skipped }),
                created.length > 0 ? 'INFO' : 'ERROR',
              );
            }
          }),
          catchError((error: HttpErrorResponse) => {
            const message = this.resolveErrorMessage(error);
            this.commonService.showToast(message, 'ERROR');
            return of(locationShippingExclusionsActions.BulkCreateLocationShippingExclusionsError({ error: message }));
          }),
        ),
      ),
    ),
  );

  // ── Delete (keyed by location_id) ─────────────────────────────────────────────

  DeleteLocationShippingExclusion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(locationShippingExclusionsActions.DeleteLocationShippingExclusion),
      mergeMap((action) =>
        this.locationShippingExclusionService.remove(action.locationId).pipe(
          map(() => locationShippingExclusionsActions.DeleteLocationShippingExclusionSuccess({ locationId: action.locationId })),
          tap(() => {
            this.commonService.showToast(this.translateService.instant('knowledge_base.shipping_exclusions.toast.delete_success'), 'SUCCESS');
          }),
          catchError((error: HttpErrorResponse) => {
            const message = this.resolveErrorMessage(error);
            this.commonService.showToast(message, 'ERROR');
            return of(locationShippingExclusionsActions.DeleteLocationShippingExclusionError({ error: message }));
          }),
        ),
      ),
    ),
  );

  // ── Bulk delete (one request for the whole batch, idempotent per id) ────────
  // An id not currently on the list is simply absent from the backend's
  // `deleted` list, never an error -- same one-Success-action-always shape
  // as bulk create.

  BulkDeleteLocationShippingExclusions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(locationShippingExclusionsActions.BulkDeleteLocationShippingExclusions),
      mergeMap((action) =>
        this.locationShippingExclusionService.bulkRemove(action.locationIds).pipe(
          map((response: APIResponse<number>) => {
            const deleted = response.data ?? [];
            const skipped = action.locationIds.length - deleted.length;
            return locationShippingExclusionsActions.BulkDeleteLocationShippingExclusionsSuccess({ deleted, skipped });
          }),
          tap(({ deleted, skipped }) => {
            if (deleted.length > 0) {
              this.commonService.showToast(
                this.translateService.instant('knowledge_base.shipping_exclusions.toast.bulk_delete_success', { count: deleted.length }),
                'SUCCESS',
              );
            }
            if (skipped > 0) {
              this.commonService.showToast(
                this.translateService.instant('knowledge_base.shipping_exclusions.toast.bulk_delete_skipped', { count: skipped }),
                deleted.length > 0 ? 'INFO' : 'ERROR',
              );
            }
          }),
          catchError((error: HttpErrorResponse) => {
            const message = this.resolveErrorMessage(error);
            this.commonService.showToast(message, 'ERROR');
            return of(locationShippingExclusionsActions.BulkDeleteLocationShippingExclusionsError({ error: message }));
          }),
        ),
      ),
    ),
  );

  // ── Helpers ────────────────────────────────────────────────────────────────

  private resolveErrorMessage(error: HttpErrorResponse): string {
    const status = error?.status;
    const backendMessage = error?.error?.message || error?.message || '';

    if (status === 409) {
      return this.translateService.instant('knowledge_base.shipping_exclusions.toast.duplicate');
    }
    return backendMessage || this.translateService.instant('knowledge_base.shipping_exclusions.toast.error');
  }
}
